import React, { useEffect, useState } from "react";
import Sidebar from "./layout/Sidebar";
import Header from "./layout/Header";
import { getDigikeyTokenExpiryApi } from "actions/digikeyAction";
import { AlertTriangle, X } from "lucide-react";
import HubHeader from "./layout/HubHeader";
import { hubHosted } from "lib/hubSso";

let digikeyTokenExpiryResult = null;
let digikeyTokenExpiryPromise = null;
let digikeyTokenBannerDismissed = false;

const getDigikeyTokenExpiredOnce = async () => {
  if (digikeyTokenExpiryResult !== null) return digikeyTokenExpiryResult;

  if (!digikeyTokenExpiryPromise) {
    digikeyTokenExpiryPromise = getDigikeyTokenExpiryApi()
      .then((response) => Boolean(response?.status && response?.data?.status))
      .then((expired) => {
        digikeyTokenExpiryResult = expired;
        digikeyTokenExpiryPromise = null;
        return expired;
      })
      .catch((error) => {
        digikeyTokenExpiryPromise = null;
        throw error;
      });
  }

  return digikeyTokenExpiryPromise;
};

const DigikeyTokenBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkTokenExpiry = async () => {
      try {
        const expired = await getDigikeyTokenExpiredOnce();
        if (mounted) setShowBanner(expired && !digikeyTokenBannerDismissed);
      } catch (error) {
        console.error("Digikey-token-expiry-check-error---", error);
      }
    };

    checkTokenExpiry();

    return () => {
      mounted = false;
    };
  }, []);

  if (!showBanner) return null;

  return (
    <div className="shrink-0 border-b border-[#F3C7CB] bg-s360-danger-bg px-4 py-2 text-[#7A1019]">
      <div className="grid grid-cols-[2rem_1fr_2rem] items-center gap-2 text-sm font-semibold">
        <span />
        <div className="flex items-center justify-center gap-2 text-center">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Digi-Key token expired. Please refresh the token before searching or ordering Digi-Key parts.</span>
        </div>
        <button
          type="button"
          onClick={() => {
            digikeyTokenBannerDismissed = true;
            setShowBanner(false);
          }}
          title="Dismiss Digi-Key token warning"
          className="inline-flex h-7 w-7 items-center justify-center justify-self-end rounded text-[#7A1019] hover:bg-[#F3C7CB] transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Each page renders its own head (title + subtitle) in the SmartBuy design
// system, so PageWrapper no longer renders the legacy `pageData` title block —
// that produced a duplicate heading on every page. `showHeader` is kept for
// backward compatibility with existing callers but no longer renders anything.
const PageWrapper = ({ children }) => {
  // Under the Hub the suite header is already on screen. Drawing the product's
  // own bar underneath it repeats the brand, the product name and the account
  // menu — which reads as two headers, not one product. The canonical pattern
  // across the suite is Hub header -> [rail | content], with no product strip
  // between them. Standalone (localhost) there is no Hub, so QBuy360 keeps its
  // own chrome untouched.
  const embedded = hubHosted();

  return (
    <div className="flex flex-col h-screen bg-s360-app">
      <HubHeader />
      {!embedded && <Header />}
      <DigikeyTokenBanner />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        {/* 18/24/24 — the suite's page padding. p-6 sat content 6px lower
            than every other product under the same Hub header. */}
        <main className="flex-1 overflow-y-auto pt-[18px] px-6 pb-6">{children}</main>
      </div>
    </div>
  );
};

export default PageWrapper;
